/**
 * FilterRail, Sprint 1.6 browse picker filter controls.
 *
 * Left rail on desktop, collapsible panel on mobile (a "Filters"
 * button toggles visibility). All filter changes fire instantly —
 * no "Apply" button. Per Wroblewski/Krug: filter chips should look
 * and behave like every other chip in the app (same shape as
 * SmartIntentBar's chips, the chat widget's quick-replies, etc.).
 */

import { useState } from "react";
import { SlidersHorizontal } from "lucide-react";

export type VendorCategory =
  | "cake"
  | "hamper"
  | "florist"
  | "workshop"
  | "spa"
  | "experience"
  | "meal"
  // MS278: coffee is now a first-class catalogue category. Patricia
  // Coffee, Proud Mary's, and ST. ALi all carry voucher SKUs under
  // this category. Without this enum value, the browse filter
  // wouldn't surface the coffee chip and the chat-concierge's
  // /browse?category=coffee handoff link would render but the page
  // would silently fall back to "Any".
  | "coffee"
  // MS-NF: non-food sentimental gifts. Drinkware (Yeti, Stanley, Frank
  // Green) + homewares (Robert Gordon). Same add-a-category pattern as
  // coffee: enum value here + CATEGORIES chip + ALLOWED_CATEGORIES in
  // Browse.tsx and DrawerBrowsePanel.tsx + the vendor_category CHECK.
  | "drinkware"
  | "homewares"
  // MS615: indoor plants (The Plant Society, Fitzroy Nursery).
  | "plants"
  // MS619: books (Readings and future book vendors).
  | "books";

export type BrowseMomentTag = "new_home" | "housewarming" | "new_baby" | "sympathy";

export type BrowseSort =
  | "featured"
  | "price_asc"
  | "price_desc"
  | "name_asc"
  | "newest";

export interface BrowseFilters {
  categories: VendorCategory[];
  /** Inclusive lower bound (null = no min). */
  priceMin: number | null;
  /** Inclusive upper bound (null = no max). */
  priceMax: number | null;
  dietary: string[];
  /** MS619: free-text search (name / description). */
  searchQuery: string;
  /** MS619: catalogue sort (Shopify-style). */
  sort: BrowseSort;
  /** MS619: gift-occasion facet (Amazon-style). null = any. */
  momentTag: BrowseMomentTag | null;
  /** MS620: results per page (Amazon/Shopify-style). */
  pageSize: 20 | 50 | 100;
  /** MS620: 1-based page index for paginated grid. */
  page: number;
  // MS314: lead-time filter. Inclusive upper bound on
  // vendor_products.lead_time_days. null = no constraint.
  // Common buckets:
  //   1 = "tomorrow" (same-day vendors only)
  //   3 = "within 3 days" (most cake studios)
  //   7 = "within a week" (almost everything)
  //   null = "any lead time" (default)
  // Pairs with MS312/313 lead-time validation in OrderConfirmSheet
  // and submit-gift-request: the filter helps customers find what's
  // deliverable; the gates prevent picking a too-soon date.
  maxLeadTimeDays: number | null;
}

interface PriceBucket {
  label: string;
  min: number | null;
  max: number | null;
}

interface LeadTimeBucket {
  label: string;
  /** Inclusive upper bound on lead_time_days. null = no constraint. */
  max: number | null;
}

// MS314: lead-time chips. Sized to match the most common vendor
// lead-time tiers in the catalogue today (same-day vouchers, cake
// studios at 2-3 days, hampers at 5-7 days, custom workshops at
// 7+ days). When the customer picks "Tomorrow", the grid narrows
// to SKUs with lead_time_days <= 1 OR is_voucher (vouchers are
// email-delivered same-day per MS312/313).
const LEAD_TIME_BUCKETS: LeadTimeBucket[] = [
  { label: "Any",          max: null },
  { label: "Tomorrow",     max: 1 },
  { label: "Within 3 days", max: 3 },
  { label: "Within a week", max: 7 },
];

// MS242: expanded from the coarse 4-bucket version (Any / Under $80
// / $80-$150 / $150+) to a 6-bucket ladder that aligns with the
// admin tier-gap report (MS241). The "Under $30" entry exists
// specifically so customers can surface budget options — Lucky's
// 2026-05-18 audit found Ask AI was anchoring all answers on $99
// when there's a $25 SKU. The browse filter should match that
// honesty by letting users actually find sub-$50 options. Today
// "Under $30" returns only the $25 Endota Gift Card; that's the
// canary signalling we need to source more lower-tier SKUs.
const PRICE_BUCKETS: PriceBucket[] = [
  { label: "Any",         min: null, max: null },
  { label: "Under $30",   min: null, max: 30 },
  { label: "$30 – $60",   min: 30,   max: 60 },
  { label: "$60 – $100",  min: 60,   max: 100 },
  { label: "$100 – $200", min: 100,  max: 200 },
  { label: "$200+",       min: 200,  max: null },
];

const CATEGORIES: { value: VendorCategory; label: string }[] = [
  { value: "cake", label: "Cake" },
  { value: "hamper", label: "Hamper" },
  { value: "florist", label: "Florist" },
  { value: "workshop", label: "Workshop" },
  { value: "spa", label: "Spa" },
  { value: "experience", label: "Experience" },
  // MS292: "Meal" chip removed from the visible filter set — zero
  // active SKUs in the catalogue (`vendor_category = 'meal'` returns
  // no rows). Tapping the chip would always show "Nothing matches yet",
  // which is dead-end UX. The 'meal' enum value stays valid in the
  // type + ALLOWED_CATEGORIES + RPC (so seeding a meal vendor later
  // requires zero schema changes); we only hide it from the public
  // filter rail until the first meal vendor lands.
  // MS278: Patricia Coffee, Proud Mary's, ST. ALi (Melbourne roasters).
  { value: "coffee", label: "Coffee & tea" },
  // MS-NF: non-food sentimental gifts.
  { value: "drinkware", label: "Drinkware" },
  { value: "homewares", label: "Homewares" },
  // MS615: live indoor plants for new home / housewarming gifts.
  { value: "plants", label: "Plants" },
  // MS619: Readings paperbacks and future book gifts.
  { value: "books", label: "Books" },
];

const MOMENT_TAGS: { value: BrowseMomentTag; label: string }[] = [
  { value: "new_home", label: "New home" },
  { value: "housewarming", label: "Housewarming" },
  { value: "new_baby", label: "New baby" },
  { value: "sympathy", label: "Sympathy" },
];

const DIETARY: { value: string; label: string }[] = [
  { value: "vegetarian", label: "Vegetarian" },
  { value: "vegan", label: "Vegan" },
  { value: "gluten_free", label: "Gluten-free" },
  { value: "dairy_free", label: "Dairy-free" },
  { value: "halal", label: "Halal" },
  { value: "kosher", label: "Kosher" },
  { value: "nut_free", label: "Nut-free" },
];

interface FilterRailProps {
  value: BrowseFilters;
  onChange: (next: BrowseFilters) => void;
  /** Compact always-visible filters for drawer fullscreen browse. */
  variant?: "default" | "compact" | "embedded";
}

// Shared chip styles, match SmartIntentBar + chat chips so the
// design language stays consistent across surfaces.
function chipClass(active: boolean, compact = false): string {
  const sizing = compact ? "px-2 py-0.5 text-[11px] leading-snug" : "px-3 py-1 text-sm";
  return `${sizing} rounded-full border transition-colors focus:outline-none focus:ring-2 focus:ring-coral ${
    active
      ? "border-coral bg-coral text-white"
      : "border-navy/20 bg-white text-navy hover:border-coral hover:text-coral"
  }`;
}

function toggle<T>(arr: T[], v: T): T[] {
  return arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
}

export function FilterRail({ value, onChange, variant = "default" }: FilterRailProps) {
  // On mobile, the rail is hidden behind a toggle. We start collapsed.
  const [mobileOpen, setMobileOpen] = useState(false);

  const activeCount =
    value.categories.length +
    (value.priceMin !== null || value.priceMax !== null ? 1 : 0) +
    value.dietary.length +
    (value.maxLeadTimeDays !== null ? 1 : 0) +
    (value.momentTag ? 1 : 0) +
    (value.searchQuery.trim() ? 1 : 0);

  // Show dietary chips only when at least one consumable category is
  // selected (cake/hamper/meal), keeps the rail short for users
  // browsing workshops/spas/experiences where it doesn't apply.
  const dietaryRelevant =
    value.categories.length === 0 ||
    value.categories.some((c) => c === "cake" || c === "hamper" || c === "meal");

  const matchedBucket = PRICE_BUCKETS.find(
    (b) => b.min === value.priceMin && b.max === value.priceMax,
  ) ?? PRICE_BUCKETS[0];

  const matchedLeadTime = LEAD_TIME_BUCKETS.find(
    (b) => b.max === value.maxLeadTimeDays,
  ) ?? LEAD_TIME_BUCKETS[0];

  const compactChips = variant === "default";

  const body = (
    <div className="min-w-0 space-y-5" style={{ fontFamily: "DM Sans, sans-serif" }}>
      {/* MS515: categories are now single-select (radio, not checkbox).
          Selecting Cake then Hamper was combining them under the same
          category-specific hero text, producing nonsense copy ("all your
          favourite real cakes" with hampers listed below). With single-
          select, categories.length is always 0 or 1 — the hero is always
          unambiguous, and the filter result always matches the headline.
          Clicking an already-active chip deselects it (back to "any"). */}
      <section>
        <h3 className="mb-2.5 text-[11px] font-semibold uppercase text-coral/85" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", letterSpacing: "0.16em" }}>
          Categories
        </h3>
        <div className="flex w-full min-w-0 flex-wrap gap-1.5">
          {CATEGORIES.map((c) => {
            const active = value.categories.includes(c.value);
            return (
              <button
                key={c.value}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() =>
                  // Single-select: clicking the active chip deselects;
                  // clicking any other chip replaces the current selection.
                  onChange({ ...value, categories: active ? [] : [c.value] })
                }
                className={`${chipClass(active, compactChips)} max-w-full`}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      </section>

      <section>
        <h3 className="mb-2.5 text-[11px] font-semibold uppercase text-coral/85" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", letterSpacing: "0.16em" }}>
          Price
        </h3>
        <div className="flex w-full min-w-0 flex-wrap gap-1.5">
          {PRICE_BUCKETS.map((b) => (
            <button
              key={b.label}
              type="button"
              onClick={() => onChange({ ...value, priceMin: b.min, priceMax: b.max })}
              aria-pressed={matchedBucket === b}
              className={chipClass(matchedBucket === b, compactChips)}
            >
              {b.label}
            </button>
          ))}
        </div>
      </section>

      {/* MS314: lead-time chip section. Pairs with MS312 client-side
          delivery-date validation in OrderConfirmSheet so customers
          can find what's actually deliverable in the timeframe they
          need. "Tomorrow" narrows to vouchers + same-day vendors;
          longer windows include progressively more of the catalogue. */}
      <section>
        <h3 className="mb-2.5 text-[11px] font-semibold uppercase text-coral/85" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", letterSpacing: "0.16em" }}>
          How soon?
        </h3>
        <div className="flex w-full min-w-0 flex-wrap gap-1.5">
          {LEAD_TIME_BUCKETS.map((b) => (
            <button
              key={b.label}
              type="button"
              onClick={() => onChange({ ...value, maxLeadTimeDays: b.max })}
              aria-pressed={matchedLeadTime === b}
              className={chipClass(matchedLeadTime === b, compactChips)}
            >
              {b.label}
            </button>
          ))}
        </div>
      </section>

      <section>
        <h3 className="mb-2.5 text-[11px] font-semibold uppercase text-coral/85" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", letterSpacing: "0.16em" }}>
          Occasion
        </h3>
        <div className="flex w-full min-w-0 flex-wrap gap-1.5">
          <button
            type="button"
            onClick={() => onChange({ ...value, momentTag: null })}
            aria-pressed={value.momentTag === null}
            className={chipClass(value.momentTag === null, compactChips)}
          >
            Any
          </button>
          {MOMENT_TAGS.map((m) => {
            const active = value.momentTag === m.value;
            return (
              <button
                key={m.value}
                type="button"
                onClick={() =>
                  onChange({ ...value, momentTag: active ? null : m.value })
                }
                aria-pressed={active}
                className={chipClass(active, compactChips)}
              >
                {m.label}
              </button>
            );
          })}
        </div>
      </section>

      {dietaryRelevant && (
        <section>
          <h3 className="mb-2.5 text-[11px] font-semibold uppercase text-coral/85" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", letterSpacing: "0.16em" }}>
            Dietary
          </h3>
          <div className="flex w-full min-w-0 flex-wrap gap-1.5">
            {DIETARY.map((d) => (
              <button
                key={d.value}
                type="button"
                onClick={() => onChange({ ...value, dietary: toggle(value.dietary, d.value) })}
                aria-pressed={value.dietary.includes(d.value)}
                className={chipClass(value.dietary.includes(d.value), compactChips)}
              >
                {d.label}
              </button>
            ))}
          </div>
        </section>
      )}

      {activeCount > 0 && (
        <button
          type="button"
          onClick={() =>
            onChange({
              categories: [],
              priceMin: null,
              priceMax: null,
              dietary: [],
              maxLeadTimeDays: null,
              searchQuery: "",
              sort: value.sort,
              momentTag: null,
              pageSize: value.pageSize,
              page: 1,
            })
          }
          className="text-xs text-charcoal/70 underline-offset-2 hover:text-coral hover:underline"
        >
          Clear filters
        </button>
      )}
    </div>
  );

  if (variant === "embedded") {
    return (
      <div
        className="rounded-2xl border border-charcoal/10 bg-white p-4 shadow-sm"
        aria-label="Browse filters"
        data-testid="filter-rail-embedded"
      >
        {body}
      </div>
    );
  }

  if (variant === "compact") {
    return (
      <div
        className="space-y-2 overflow-x-auto pb-1"
        style={{ fontFamily: "DM Sans, sans-serif" }}
        aria-label="Browse filters"
      >
        <div className="flex min-w-max flex-wrap items-center gap-1.5">
          <span
            className="mr-1 text-[10px] font-semibold uppercase text-coral/85"
            style={{
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              letterSpacing: "0.14em",
            }}
          >
            Category
          </span>
          {CATEGORIES.map((c) => {
            const active = value.categories.includes(c.value);
            return (
              <button
                key={c.value}
                type="button"
                onClick={() =>
                  onChange({ ...value, categories: active ? [] : [c.value] })
                }
                className={chipClass(active)}
              >
                {c.label}
              </button>
            );
          })}
        </div>
        <div className="flex min-w-max flex-wrap items-center gap-1.5">
          <span
            className="mr-1 text-[10px] font-semibold uppercase text-coral/85"
            style={{
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              letterSpacing: "0.14em",
            }}
          >
            Price
          </span>
          {PRICE_BUCKETS.map((b) => (
            <button
              key={b.label}
              type="button"
              onClick={() => onChange({ ...value, priceMin: b.min, priceMax: b.max })}
              aria-pressed={matchedBucket === b}
              className={chipClass(matchedBucket === b)}
            >
              {b.label}
            </button>
          ))}
        </div>
        <div className="flex min-w-max flex-wrap items-center gap-1.5">
          <span
            className="mr-1 text-[10px] font-semibold uppercase text-coral/85"
            style={{
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              letterSpacing: "0.14em",
            }}
          >
            How soon
          </span>
          {LEAD_TIME_BUCKETS.map((b) => (
            <button
              key={b.label}
              type="button"
              onClick={() => onChange({ ...value, maxLeadTimeDays: b.max })}
              aria-pressed={matchedLeadTime === b}
              className={chipClass(matchedLeadTime === b)}
            >
              {b.label}
            </button>
          ))}
          {dietaryRelevant &&
            DIETARY.map((d) => (
              <button
                key={d.value}
                type="button"
                onClick={() => onChange({ ...value, dietary: toggle(value.dietary, d.value) })}
                aria-pressed={value.dietary.includes(d.value)}
                className={chipClass(value.dietary.includes(d.value))}
              >
                {d.label}
              </button>
            ))}
          {activeCount > 0 && (
            <button
              type="button"
              onClick={() =>
                onChange({
                  categories: [],
                  priceMin: null,
                  priceMax: null,
                  dietary: [],
                  maxLeadTimeDays: null,
                  searchQuery: "",
                  sort: value.sort,
                  momentTag: null,
                  pageSize: value.pageSize,
                  page: 1,
                })
              }
              className="ml-1 text-xs text-charcoal/70 underline-offset-2 hover:text-coral hover:underline"
            >
              Clear
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Mobile: toggle button + collapsible panel */}
      <div className="lg:hidden">
        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          aria-expanded={mobileOpen}
          aria-controls="filter-rail-mobile"
          className="inline-flex items-center gap-2 rounded-full border-2 border-navy/15 bg-white px-4 py-2 text-sm font-semibold text-navy hover:border-coral hover:text-coral focus:outline-none focus:ring-2 focus:ring-coral"
          style={{ fontFamily: "DM Sans, sans-serif" }}
        >
          <SlidersHorizontal size={16} strokeWidth={2.5} />
          Filters
          {activeCount > 0 && (
            <span className="ml-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-coral px-1.5 text-xs text-white">
              {activeCount}
            </span>
          )}
        </button>
        {mobileOpen && (
          <div
            id="filter-rail-mobile"
            className="mt-3 rounded-2xl border-2 border-charcoal/10 bg-white p-4 shadow-sm"
          >
            {body}
          </div>
        )}
      </div>

      {/* Desktop: persistent left rail — MS620 widened to lg:w-72 so chips
          stay inside the white container (240px was overflowing). */}
      <aside
        className="hidden min-w-0 lg:block lg:w-72 lg:shrink-0"
        aria-label="Browse filters"
      >
        <div className="sticky top-20 max-h-[calc(100dvh-6rem)] overflow-x-hidden overflow-y-auto rounded-2xl border-2 border-charcoal/10 bg-white p-4 shadow-sm">
          {body}
        </div>
      </aside>
    </>
  );
}
